import React from 'react';
import { Box, IconButton, Text, Flex, Circle } from '@chakra-ui/react';
import { FiX, FiMic, FiMicOff } from 'react-icons/fi';

import { ChevronUpIcon } from '@chakra-ui/icons';
import { trackingService } from '../../../../shared/eventTracking';

import '../../../../containers/prescription/animations.css';

interface MinimizedModalProps {
  audioData: number[];
  recordingTime: number;
  toggleRecording: () => void;
  isMuted: boolean;
  toggleMute: () => void;
  setIsMinimized: (minimized: boolean) => void;
  MAX_RECORDING_TIME: number;
  onClose: () => void;
  getElapsedTime: () => number;
  isMobile: boolean | undefined;
}

function MinimizedModal({
  audioData,
  recordingTime,
  toggleRecording,
  isMuted,
  toggleMute,
  setIsMinimized,
  MAX_RECORDING_TIME,
  onClose,
  getElapsedTime,
  isMobile,
}: MinimizedModalProps) {
  return (
    <Box
      position="fixed"
      bottom={isMobile ? '15%' : '10%'}
      right={isMobile ? '20px' : '125px'}
      zIndex={11}
      bg="white"
      borderRadius="12px"
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
      border="1px solid"
      borderColor="gray.100"
      minWidth="400px"
      maxWidth="400px"
      height="100px"
      transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
      transform="scale(1) translateY(0px)"
      opacity="1"
      animation="slideInFromBottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      <Flex direction="column" h="100%" px="16px" py="12px">
        <Flex align="center" gap="12px" mb="12px">
          <IconButton
            aria-label="Fechar"
            icon={<FiX />}
            size="sm"
            variant="ghost"
            bg="purple.200"
            color="purple.600"
            borderRadius="50%"
            _hover={{ bg: 'purple.300' }}
            onClick={onClose}
            minW="32px"
            h="32px"
          />

          <IconButton
            aria-label={isMuted ? 'Desmutar microfone' : 'Mutar microfone'}
            icon={isMuted ? <FiMicOff /> : <FiMic />}
            size="sm"
            variant="ghost"
            bg={isMuted ? 'red.100' : 'purple.200'}
            color={isMuted ? 'red.600' : 'purple.600'}
            borderRadius="50%"
            _hover={{ bg: isMuted ? 'red.200' : 'purple.300' }}
            onClick={toggleMute}
            minW="32px"
            h="32px"
          />

          <Flex align="center" justify="space-between" flex="1" h="32px" overflow="hidden" px="2px">
            {audioData.map((height, index) => {
              // Normalizar para a versão minimizada (altura máxima de 24px para caber nos 32px do container)
              const normalizedHeight = Math.max(4, Math.pow(height / 255, 0.8) * 20 + 4);
              return (
                <Box
                  key={index}
                  width="3px"
                  height={`${normalizedHeight}px`}
                  bg="purple.600"
                  borderRadius="1px"
                  transition="height 0.1s ease-out"
                  opacity={0.9}
                />
              );
            })}
          </Flex>

          <Circle
            size="40px"
            border="3px solid"
            borderColor="purple.200"
            bg="purple.600"
            cursor="pointer"
            _hover={{ bg: 'purple.700' }}
            onClick={toggleRecording}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box bg="white" w="12px" h="12px" borderRadius="2px" />
          </Circle>

          <IconButton
            aria-label="Maximizar"
            icon={<ChevronUpIcon />}
            size="sm"
            variant="ghost"
            bg="pink.200"
            color="purple.600"
            borderRadius="50%"
            _hover={{ bg: 'pink.300' }}
            onClick={() => {
              trackingService.trackPrescriptionVoiceWindowToggle('maximize');
              setIsMinimized(false);
            }}
            minW="32px"
            h="32px"
          />
        </Flex>

        <Flex direction="column" gap="8px">
          <Box position="relative" h="4px" bg="pink.200" borderRadius="2px" overflow="hidden">
            <Box
              h="100%"
              bg="pink.400"
              borderRadius="2px"
              width={`${(getElapsedTime() / MAX_RECORDING_TIME) * 100}%`}
              className="smooth-progress-bar"
            />
          </Box>
          <Flex justify="space-between" fontSize="18px" color="purple.600" fontWeight="500">
            <Text>{Math.floor(getElapsedTime())}s</Text>
            <Text>-{Math.ceil(recordingTime)}s</Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

export { MinimizedModal };
